import { Module } from 'vuex';
import { RootState } from '@/store';
import { OrderSparePart } from '@/services/order-spare-parts/order-spare-parts.types';
import { OrderSparePartsService } from '@/services/order-spare-parts/order-spare-parts.service';

const orderSparePartsService = new OrderSparePartsService();

export interface OrderSparePartState {
  orderSparePart: OrderSparePart;
  orderSparePartLoading: boolean;
  orderLabelLoading: boolean;
}

const state: OrderSparePartState = {
  orderSparePart: null,
  orderSparePartLoading: false,
  orderLabelLoading: false
};

const options: Module<OrderSparePartState, RootState> = {
  namespaced: true,
  state: () => state,
  actions: {
    setOrderSparePart: ({ commit, dispatch }, orderSparePart: OrderSparePart | number): Promise<void> => {
      if (typeof orderSparePart === 'number') return dispatch('getOrderSparePart', orderSparePart);
      else commit('setOrderSparePart', orderSparePart);
    },
    getOrderSparePart: ({ commit, dispatch }, payload: number | { id: number; deviceId?: number }): Promise<void> => {
      const id = typeof payload === 'number' ? payload : payload.id;
      const deviceId = typeof payload === 'number' ? undefined : payload.deviceId;
      commit('setOrderSparePartLoading', true);
      return orderSparePartsService
        .get(id, deviceId)
        .then(osp => osp && commit('setOrderSparePart', osp))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('setOrderSparePartLoading', false));
    },
    finishOrderSparePart: ({ commit, dispatch }, payload: { id: number; bac?: string }): Promise<void> => {
      commit('setOrderSparePartLoading', true);
      return orderSparePartsService
        .finish(payload.id, payload.bac)
        .then(osp => osp && commit('setOrderSparePart', osp))
        .then(() => dispatch('alert/pushSuccess', 'Commande de pièces terminée', { root: true }))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('setOrderSparePartLoading', false));
    },
    markOrderSparePartsOutOfStock: (
      { commit, dispatch },
      payload: { id: number; sparePartIds: number[] }
    ): Promise<void> => {
      commit('setOrderSparePartLoading', true);
      return orderSparePartsService
        .markOutOfStock(payload.id, payload.sparePartIds)
        .then(osp => osp && commit('setOrderSparePart', osp))
        .then(() => dispatch('alert/pushSuccess', 'Pieces marquees hors stock', { root: true }))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('setOrderSparePartLoading', false));
    },
    getOrderSparePartByBac: (
      { commit, dispatch },
      payload: string | { bac: string; serialNo?: string }
    ): Promise<void> => {
      const bac = typeof payload === 'string' ? payload : payload.bac;
      const serialNo = typeof payload === 'string' ? undefined : payload.serialNo;
      commit('setOrderSparePartLoading', true);
      return orderSparePartsService
        .getByBac(bac, serialNo)
        .then(osp => osp && commit('setOrderSparePart', osp))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('setOrderSparePartLoading', false));
    },
    generateOfDolibarr: ({ dispatch }, deviceId: number): Promise<void> => {
      return orderSparePartsService
        .getByDevice(deviceId)
        .then(osps => {
          if (!osps || osps.length === 0) return;
          return Promise.all(osps.map(osp => orderSparePartsService.generateOfDolibarr(osp.id))).then(() => undefined);
        })
        .catch(error => dispatch('alert/pushError', error, { root: true }));
    },
    putInStock: ({ commit, dispatch }, id: number): Promise<void> => {
      commit('setOrderSparePartLoading', true);
      return orderSparePartsService
        .putInStock(id)
        .then(osp => osp && commit('setOrderSparePart', osp))
        .then(() => dispatch('alert/pushSuccess', 'Commande mise en stock', { root: true }))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('setOrderSparePartLoading', false));
    }
  },
  mutations: {
    setOrderSparePart: (state, orderSparePart: OrderSparePart) => (state.orderSparePart = orderSparePart),
    setOrderSparePartLoading: (state, loading: boolean) => (state.orderSparePartLoading = loading),
    setOrderLabelLoading: (state, loading: boolean) => (state.orderLabelLoading = loading)
  }
};

export default options;
